import dayjs from "dayjs";
import { formatAmount } from "./ServiceHelpers";

/**
 * Parse address string into components for invoice display
 * Format should be: Line1 = Street, Line2 = Suite (if present), Last line = City, State ZIP
 */
export const parseAddress = (addressString) => {
  if (!addressString) {
    return { line1: "", line2: "", zipCode: "" };
  }

  // If multiple addresses separated by " | ", take only the first one
  if (addressString.includes(" | ")) {
    addressString = addressString.split(" | ")[0].trim();
  }
  
  // Split by commas
  const parts = addressString.split(",").map((part) => part.trim());

  if (parts.length >= 3) {
    // The last part should contain State ZIP (e.g., "TX 78731")
    const lastPart = parts[parts.length - 1];
    const stateZipParts = lastPart.split(" ").filter((p) => p);
    const zipCode = stateZipParts[stateZipParts.length - 1];
    const state = stateZipParts.slice(0, -1).join(" ");

    // The second-to-last part should be the city
    const city = parts[parts.length - 2];

    // Everything before the city is the street address (may include suite, apt, etc.)
    let streetParts = parts.slice(0, -2);

    // Handle cases where suite abbreviations are separated from their numbers
    // e.g., ["5900 Balcones Drive", "Ste", "8078"] should become ["5900 Balcones Drive", "Ste 8078"]
    const suiteAbbreviations = /^(Ste|Suite|Apt|Apartment|Unit|#)\.?$/i;
    for (let i = 0; i < streetParts.length - 1; i++) {
      if (suiteAbbreviations.test(streetParts[i])) {
        // Combine this part with the next part
        streetParts[i] = `${streetParts[i]} ${streetParts[i + 1]}`;
        streetParts.splice(i + 1, 1);
        break;
      }
    }

    // If there are multiple street parts, first is street address, rest is suite/apt
    if (streetParts.length > 1) {
      return {
        line1: streetParts[0],
        line2: streetParts.slice(1).join(", "),
        zipCode: `${city}, ${state} ${zipCode}`,
      };
    } else {
      // No suite number
      return {
        line1: streetParts[0],
        line2: "",
        zipCode: `${city}, ${state} ${zipCode}`,
      };
    }
  } else if (parts.length === 2) {
    // Fallback for incomplete addresses
    return {
      line1: parts[0],
      line2: "",
      zipCode: parts[1],
    };
  } else {
    return {
      line1: addressString,
      line2: "",
      zipCode: "",
    };
  }
};

/**
 * Convert duration string (HH:MM:SS) to decimal hours with proper formatting
 */
export const convertDurationToDecimal = (durationString) => {
  if (!durationString) return "0 hours";

  const parts = durationString.split(":");
  if (parts.length === 3) {
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseInt(parts[2], 10) || 0;
    const decimalHours = hours + minutes / 60 + seconds / 3600;
    const rounded = Math.round(decimalHours * 100) / 100;
    const formatted =
      rounded % 1 === 0 ? Math.floor(rounded) : rounded.toFixed(2);
    const unit = parseFloat(formatted) === 1 ? "hour" : "hours";
    return `${formatted} ${unit}`;
  }
  return durationString;
};

/**
 * Merge OPI services that differ only by language
 */
export const mergeOPIByLanguage = (items) => {
  if (!items || items.length === 0) return items;

  const grouped = {};
  const nonOPI = [];

  items.forEach((item, index) => {
    if (item?.isFee || item?.isMileage) {
      nonOPI.push({ ...item, originalIndex: index });
      return;
    }
    // Check if this is an OPI service
    const isOPI = item.serviceCategory === "Interpretation" && item.details;

    if (!isOPI) {
      nonOPI.push({ ...item, originalIndex: index });
      return;
    }

    // Extract the parts: PREFIX - Phone LANGUAGE Interpretation
    // Pattern: "CLIENTNAME - SUBACCOUNT - ... - Monthly On-Demand Phone LANGUAGE Interpretation"
    const phoneMatch = item.details.match(
      /^(.+?)\s+Phone\s+(.+?)\s+Interpretation(.*)$/,
    );

    if (!phoneMatch) {
      nonOPI.push({ ...item, originalIndex: index });
      return;
    }

    const prefix = phoneMatch[1]; // Everything before "Phone"
    const language = phoneMatch[2]; // The language
    const suffix = phoneMatch[3]; // Everything after "Interpretation"

    // Group by everything except the language
    const groupKey = `${item.date}|${prefix}|${suffix}|${item.rate}`;

    if (!grouped[groupKey]) {
      grouped[groupKey] = {
        ...item,
        originalIndex: index,
        prefix: prefix,
        suffix: suffix,
        languages: [language],
        projectNumbers: [item.projectNumber],
        units:
          item.unit !== "N/A" && item.unit ? [parseFloat(item.unit) || 0] : [0],
        totals: [parseFloat(item.total.replace(/[^0-9.-]/g, "")) || 0],
      };
    } else {
      grouped[groupKey].languages.push(language);
      grouped[groupKey].projectNumbers.push(item.projectNumber);
      if (item.unit !== "N/A" && item.unit) {
        const unitValue = parseFloat(item.unit);
        if (!isNaN(unitValue)) {
          grouped[groupKey].units.push(unitValue);
        }
      }
      grouped[groupKey].totals.push(
        parseFloat(item.total.replace(/[^0-9.-]/g, "")) || 0,
      );
    }
  });

  // Convert grouped items back to array format
  const mergedOPI = Object.values(grouped).map((group) => {
    // Format language list
    let languageList;
    if (group.languages.length === 1) {
      languageList = group.languages[0];
    } else if (group.languages.length === 2) {
      languageList = group.languages.join(" and ");
    } else {
      const lastLang = group.languages[group.languages.length - 1];
      const otherLangs = group.languages.slice(0, -1).join(", ");
      languageList = `${otherLangs}, and ${lastLang}`;
    }

    const totalUnits = group.units.reduce((sum, val) => sum + val, 0);
    const totalAmount = group.totals.reduce((sum, val) => sum + val, 0);
    const mergedDetails = `${group.prefix} Phone ${languageList} Interpretation${group.suffix}`;
    const projectNumber = group.projectNumbers[0];

    return {
      ...group,
      projectNumber: projectNumber,
      details: mergedDetails,
      unit: totalUnits > 0 ? totalUnits.toFixed(2) : "N/A",
      total: formatAmount(totalAmount.toFixed(2)),
    };
  });

  // Combine and sort by original index to maintain order
  const allItems = [...nonOPI, ...mergedOPI].sort(
    (a, b) => a.originalIndex - b.originalIndex,
  );

  return allItems;
};

/**
 * Generate items list from invoice services
 */
export const generateItemsFromServices = (
  services,
  splitMileage = false,
  mileageRates = [],
) => {
  if (!services || services.length === 0) return [];

  let items = services.map((service) => {
    const isInterpretation = service.ServiceCategory === "Interpretation";
    const isOPIWithNoDuration =
      (service.TotalDuration === "00:00:00" || service.TotalDuration === 0) &&
      service.InterpretationType === "OPI";
    const hasWordCount = service.WordCount && service.WordCount > 0;
    const hasMileageCost =
      service.MileageCost && parseFloat(service.MileageCost) > 0;

    return {
      serviceId: service.id,
      serviceCategory: service.ServiceCategory,
      projectNumber: service.ProjectNumber,
      date: dayjs(service.Date).format("MM-DD-YYYY"),
      details: service.Details,
      unit: isInterpretation
        ? isOPIWithNoDuration
          ? "N/A"
          : convertDurationToDecimal(service.TotalDuration)
        : hasWordCount
          ? `${parseFloat(service.WordCount).toLocaleString("en-US", {
              maximumFractionDigits: 0,
            })} words`
          : "N/A",
      rate: isOPIWithNoDuration
        ? "N/A"
        : isInterpretation
          ? (() => {
              const rate =
                service.ServiceClientRate !== null &&
                service.ServiceClientRate !== undefined
                  ? service.ServiceClientRate
                  : service.Client?.ClientRates?.[0]?.Rate;
              if (rate === 0) return "N/A";
              return rate !== null && rate !== undefined
                ? formatAmount(rate)
                : "N/A";
            })()
          : (() => {
              const rate = service.Rate;
              if (rate === 0) return "N/A";
              return rate !== null && rate !== undefined
                ? formatAmount(rate)
                : "N/A";
            })(),
      total: formatAmount(service.AccountCharge),
      hasMileageCost: hasMileageCost,
      mileageCost: service.MileageCost,
      mileageRoundtrip: service.MileageRoundtrip,
      serviceDate: service.Date,
    };
  });

  // Handle split mileage if requested
  if (splitMileage) {
    const detailedItems = [];
    items.forEach((item) => {
      if (item.hasMileageCost) {
        const chargeWithoutMileage =
          parseFloat(item.total.replace(/[^0-9.-]/g, "")) -
          parseFloat(item.mileageCost);

        detailedItems.push({
          ...item,
          total: formatAmount(chargeWithoutMileage.toFixed(2)),
        });

        // Get mileage rate for the service date's year
        const serviceYear = dayjs(item.serviceDate).year();
        const mileageRateForYear = mileageRates.find(
          (rate) => rate.year === serviceYear,
        );
        const mileageRateAmount = mileageRateForYear
          ? formatAmount(mileageRateForYear.rate)
          : "$0.00";

        detailedItems.push({
          serviceId: item.serviceId,
          serviceCategory: item.serviceCategory,
          projectNumber: item.projectNumber,
          date: item.date,
          details: item.details + " - Mileage",
          unit: (item.mileageRoundtrip || 0) + " miles",
          rate: mileageRateAmount,
          total: formatAmount(item.mileageCost),
        });
      } else {
        detailedItems.push(item);
      }
    });
    items = detailedItems;
  }

  return items;
};
